/**
 * Design system de Dx Tech.
 *
 * Los valores salen del PPT de propuesta comercial de Dx Tech: se extrajeron los
 * colores declarados en el XML de las slides y se verificaron por muestreo de
 * píxeles sobre el logo y la barra de gradiente de marca.
 *
 * Estos tokens son el tema por defecto de Trellis. Un tenant (pharma) sobreescribe
 * el subconjunto semántico desde su editor de branding — ver `resolveTenantTheme`.
 */
/** Colores literales de la marca. No usar directo en componentes: usar los semánticos. */
export const brand = {
    /** Azul Dx Tech. Es el color del wordmark "Tech" y del cuarto de círculo del isotipo. */
    blue: '#335CFF',
    /** Magenta del cuadrado del isotipo. El acento de la marca. */
    magenta: '#E577FB',
    /** Celeste del semicírculo del isotipo. */
    sky: '#78C4FB',
    /** Navy del wordmark "Dx" y de los trazos de los iconos. Es el color de texto. */
    navy: '#23293B',
    /** Lila muy claro. Fondo de secciones destacadas en el PPT. */
    lilac: '#FDF2FF',
    white: '#FFFFFF',
};
/**
 * Gradiente de marca: magenta → azul → celeste.
 * Es el elemento más reconocible del PPT — aparece como barra separadora.
 */
export const brandGradient = `linear-gradient(135deg, ${brand.magenta} 0%, ${brand.blue} 55%, ${brand.sky} 100%)`;
/**
 * Fondo de las secciones oscuras: manchas radiales sobre el navy de marca.
 * Le da profundidad a un bloque que si no sería un rectángulo de color plano.
 */
export const brandHeroGradient = 'radial-gradient(1100px 560px at 18% 0%, rgba(229, 119, 251, 0.32), transparent 60%), ' +
    'radial-gradient(900px 460px at 88% 28%, rgba(51, 92, 255, 0.42), transparent 62%), ' +
    'linear-gradient(180deg, #1a1f2e 0%, #23293b 100%)';
/**
 * Escala de grises derivada del navy de marca, no grises neutros genéricos.
 * Mantiene el tinte azulado del navy para que todo se lea como una sola familia.
 */
export const neutral = {
    50: '#F7F8FA',
    100: '#EFF1F5',
    200: '#E4E7EE',
    300: '#CFD4E0',
    400: '#9AA2B7',
    500: '#6E7789',
    600: '#5B6478',
    700: '#414A5E',
    800: '#2F3648',
    900: brand.navy,
};
/**
 * Colores de estado. Deliberadamente fuera de la paleta de marca: en una app
 * clínica, "peligro" no puede confundirse con "color corporativo".
 */
export const status = {
    success: '#0E9F6E',
    successSurface: '#E6F6F0',
    warning: '#D97706',
    warningSurface: '#FEF3E2',
    danger: '#DC2626',
    dangerSurface: '#FDECEC',
    info: brand.blue,
    infoSurface: '#EBF0FF',
};
/**
 * Severidad de alertas clínicas. Escala propia y monótona: cada escalón se lee
 * como "peor" que el anterior sin depender del texto.
 *
 * CRITICAL existe separado de HIGH porque hay reglas que exigen contacto inmediato
 * (por ejemplo el ítem 9 del PHQ-9, ideación suicida) y no pueden competir
 * visualmente con una alerta de adherencia.
 */
export const severity = {
    INFO: { fg: '#1E4BD8', bg: '#EBF0FF', label: 'Informativa' },
    LOW: { fg: '#0A7EA4', bg: '#E4F4FA', label: 'Baja' },
    MEDIUM: { fg: '#B45309', bg: '#FEF3E2', label: 'Media' },
    HIGH: { fg: '#C2410C', bg: '#FFEDE4', label: 'Alta' },
    CRITICAL: { fg: '#B91C1C', bg: '#FDECEC', label: 'Crítica' },
};
export const SEVERITY_ORDER = ['INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
/**
 * Urbanist es la tipografía del PPT (Light 300 / Regular 400 / SemiBold 600).
 * Es Google Font, licencia OFL, sin costo.
 */
export const typography = {
    fontFamily: "'Urbanist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700&display=swap',
    weights: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700 },
};
export const radius = {
    sm: '8px',
    md: '12px',
    lg: '20px',
    xl: '28px',
    full: '9999px',
};
/**
 * Tokens semánticos: lo que consumen los componentes y lo que un tenant puede pisar.
 * Las claves son los nombres de las variables CSS sin el prefijo `--tr-`.
 */
export const semanticTokens = {
    primary: brand.blue,
    'primary-fg': brand.white,
    'primary-hover': '#2449D6',
    'primary-soft': '#EBF0FF',
    accent: brand.magenta,
    'accent-fg': brand.navy,
    'accent-soft': brand.lilac,
    secondary: brand.sky,
    'secondary-fg': brand.navy,
    'secondary-soft': '#E9F5FE',
    bg: brand.white,
    surface: neutral[50],
    'surface-raised': brand.white,
    'surface-tinted': brand.lilac,
    'surface-inverted': brand.navy,
    fg: brand.navy,
    'fg-muted': neutral[600],
    'fg-subtle': neutral[400],
    'fg-inverted': brand.white,
    border: neutral[200],
    'border-strong': neutral[300],
    success: status.success,
    'success-soft': status.successSurface,
    warning: status.warning,
    'warning-soft': status.warningSurface,
    danger: status.danger,
    'danger-soft': status.dangerSurface,
    info: status.info,
    'info-soft': status.infoSurface,
    'font-sans': typography.fontFamily,
    'radius-md': radius.md,
    'radius-lg': radius.lg,
    gradient: brandGradient,
};
/** Subconjunto que una pharma puede personalizar desde la consola. */
export const TENANT_OVERRIDABLE_TOKENS = [
    'primary',
    'primary-fg',
    'primary-hover',
    'primary-soft',
    'accent',
    'accent-fg',
    'accent-soft',
    'secondary',
    'secondary-fg',
    'bg',
    'surface',
    'surface-tinted',
    'fg',
    'fg-muted',
    'font-sans',
    'radius-md',
    'radius-lg',
    'gradient',
];
export const dxTechTheme = {
    brand,
    brandGradient,
    neutral,
    status,
    severity,
    typography,
    radius,
    tokens: semanticTokens,
};
//# sourceMappingURL=tokens.js.map